import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Search, Target, BarChart3, ShieldCheck } from 'lucide-react';
import { useResponsiveAnimation } from '../hooks/useResponsiveAnimation';
import { useForceAnimation } from '../hooks/useForceAnimation';

const Approach = () => {
  const { duration, baseDelay, staggerDelay } = useResponsiveAnimation();

  const [ref, inView] = useInView({
    triggerOnce: false,
    threshold: 0,
    rootMargin: '0px 0px -10% 0px',
  });

  const isVisible = useForceAnimation(inView);

  const steps = [
    {
      icon: <Search className="text-primary-600" size={24} />,
      title: 'Business Fit',
      description: 'We review whether the company is genuinely internet-native and aligned with our focus on AI-related SaaS and online businesses.',
    },
    {
      icon: <Target className="text-primary-600" size={24} />,
      title: 'Diligence',
      description: 'We study the product, customers, distribution, operations, and transition context before moving deeper into any opportunity.',
    },
    {
      icon: <BarChart3 className="text-primary-600" size={24} />,
      title: 'Transition Planning',
      description: 'When there is a fit, we define the handoff, founder involvement, and the near-term execution priorities for the business.',
    },
    {
      icon: <ShieldCheck className="text-primary-600" size={24} />,
      title: 'Build & Optimize',
      description: 'After acquisition, the goal is to strengthen product quality, execution, and long-term operating leverage inside the business.',
    },
  ];

  return (
    <section id="approach" className="py-20 pb-32 bg-gradient-to-b from-white to-gray-50 relative z-0">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -30 }}
            animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
            transition={{ duration, delay: baseDelay }}
          >
            <span className="inline-block py-1 px-3 rounded-full bg-primary-100 text-primary-600 text-sm font-medium mb-4">
              Our Approach
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">How We Evaluate Digital Opportunities</h2>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              Our process is built around digital-business fit, practical diligence, and clear transition thinking. We
              are focused on acquiring and improving online businesses with strong product and operating upside.
            </p>

            <div className="space-y-6">
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ duration, delay: baseDelay + staggerDelay * index }}
                  className="flex items-start"
                >
                  <div className="mr-4 mt-1 bg-primary-50 p-2 rounded-lg">{step.icon}</div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
            transition={{ duration, delay: baseDelay }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="/charts-and-graphs.jpeg"
                alt="ACSE Capital acquisition approach"
                className="w-full h-auto object-cover"
                style={{ maxHeight: '400px' }}
              />

              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-start">
                <div className="p-4 md:p-8 w-full mt-4">
                  <p className="text-white text-base md:text-lg font-medium">
                    "We care about product quality, customer fit, and whether the business can be made stronger after transition."
                  </p>
                </div>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration, delay: baseDelay * 1.5 }}
              className="absolute -bottom-6 -right-6 bg-white p-4 md:p-6 rounded-xl shadow-lg experience-box"
            >
              <h4 className="text-xl md:text-2xl font-bold mb-1 md:mb-2">AI + SaaS</h4>
              <p className="text-sm md:text-base text-gray-600">online-business acquisition focus</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Approach;
